import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import {
  getChannelPartnerList,
  updateChannelPartnerById,
} from "action/channelPartner";
import { useDispatch, useSelector } from "react-redux";
import { channelPartnerList } from "constants/channelPartner";
import {
  showAlert,
  ISOToHumanReadableDate,
} from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

function ChannelListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const channelPartnerListData = useSelector(
    (state) => state.channelPartner.channelPartnerList,
  );

  const [listState, setListState] = useState({
    isLoading: false,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [filterState, setFilterState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    status: null,
    filterParams: [],
  });

  const [pageNum, setPageNum] = useState(1);

  useEffect(() => {
    getChannelPartnerList(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({ type: channelPartnerList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (channelPartnerListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: channelPartnerListData?.data?.channelPartners?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      channelPartnerListData?.code ||
      channelPartnerListData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          channelPartnerListData?.message ||
          "Failed to load channel partner list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [channelPartnerListData]);

  const formattedChannelList =
    channelPartnerListData?.data?.channelPartners?.map((item, index) => ({
      id: item.ID,
      srNo:
        (pageNum - 1) *
          (channelPartnerListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,

      organizationName: item.organizationName,
      contactPerson: item.userID?.fullName || "-",
      email: item.userID?.email || "-",
      phone: item.userID?.mobile || "-",
      status: item.userID?.status || "-",
      createdOn: item.userID?.createdOn || "",
    })) || [];

  const toggleStatus = (channelPartner) => {
    const { organizationName, status, id } = channelPartner;

    const isActive = status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this channel partner?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            showLoaderOnClick: true,
          }));
          const formData = new FormData();

          formData.append(
            "channelpartner",
            JSON.stringify({
              status: newStatus,
            }),
          );

          const res = await updateChannelPartnerById(id, formData);

          if (res?.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );
            getChannelPartnerList(dispatch, pageNum, filterState.filterParams);
          }
        } catch (error) {
          notify("danger", "Something went wrong!");
        } finally {
          setListState((prev) => ({
            ...prev,
            showLoaderOnClick: false,
          }));
        }
      },
    });
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "FullName",
        condition: "match",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    if (filterState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Company Name",
      accessor: "organizationName",
    },
    {
      Header: "Contact Person",
      accessor: "contactPerson",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Mobile No",
      accessor: "phone",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Registered On",
      accessor: "createdOn",
      Cell: ({ value }) => ISOToHumanReadableDate(value),
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.id); // encode ID here //value
        const status = row.original.status;
        const isActive = status === "ACTIVE";

        return (
          <div className="actions-right d-flex justify-content-start align-items-center">
            {/* View */}
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`view${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/channel-partner/${encodedId}/details`
                    : `/admin/channel-partner/${encodedId}/details`,
                );
              }}
            >
              <i className="fa fa-eye mr-1 table-action-primary" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${encodedId}`}>
              View
            </UncontrolledTooltip>
            {/* Divider */}
            {/* <span className="mx-1 text-muted">|</span> */}

            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`edit${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/channel-partner/edit/${encodedId}`
                    : `/admin/channel-partner/edit/${encodedId}`,
                  {
                    state: { mode: "edit" },
                  },
                );
              }}
            >
              <i className="fas fa-edit m-0 ml-1 table-action-info" />
              {/* Edit */}
            </Button>
            <UncontrolledTooltip target={`edit${encodedId}`}>
              Edit
            </UncontrolledTooltip>
            {/* Divider */}
            {/* <span className="mx-1 text-muted">|</span> */}
            {/* Activate / Deactivate */}
            <span className="mx-1 text-muted">|</span>
            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${encodedId}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              {isActive ? (
                <>
                  <i className="fa-regular fa-circle-xmark table-action-delete mr-1" />
                </>
              ) : (
                <>
                  <i className="fa-regular fa-circle-check table-action-success mr-1" />
                </>
              )}
            </Button>
            <UncontrolledTooltip delay={0} target={`status${encodedId}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleOnRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.id); //rowData.ID
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/channel-partner/${encodedId}/details`
        : `/admin/channel-partner/${encodedId}/details`,
    );
  };
  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0">
                  Channel Partners
                </CardTitle>

                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? `/super-admin/channel-partner/add-new`
                        : `/admin/channel-partner/add-new`,
                      {
                        state: { mode: "add" },
                      },
                    )
                  }
                >
                  <i className="fa fa-plus mr-1" />
                  Channel Partner
                </Button>
              </CardHeader>

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchEmail}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by mobile no."
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchMobile}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchMobile: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      options={STATUS_OPTIONS}
                      placeholder="Select Status"
                      className="mr-2 mb-2 filter-select"
                      value={filterState.status}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                    >
                      {listState.isLoading ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="ml-2 mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {channelPartnerListData?.data?.totalRecords > 0 ? (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="font-weight-bold">
                          Showing{" "}
                          {channelPartnerListData?.data?.totalFilteredRecords}{" "}
                          of {channelPartnerListData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {channelPartnerListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer">{listState.error}</div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No channel partners added yet.
                  </div>
                ) : (
                  <CommonTable
                    columns={columns}
                    data={formattedChannelList}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (channelPartnerListData?.data?.totalFilteredRecords ||
                        0) /
                        (channelPartnerListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                    showPaginationBottom
                    rowClick={handleOnRowClick}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ChannelListing;
